<template>
  <a-modal
    :visible="visible"
    :maskClosable="false"
    :title="
      currentStep === 1 ? '添加账号 - 输入手机号' : '添加账号 - 验证码确认'
    "
    @cancel="handleCancel"
    :footer="null"
    width="500px"
    :focusTriggerAfterClose="false"
    :autofocus="false"
    :destroyOnClose="true"
    :wrap-style="{ overflow: 'hidden' }"
  >
    <!-- 步骤1：输入手机号 -->
    <div v-if="currentStep === 1" :tabindex="-1" class="form-container">
      <a-form :model="formState" layout="vertical">
        <a-form-item label="手机号码" :rules="[{ required: true, validator: validatePhone }]">
          <a-input
            v-model:value="formState.phoneNumber"
            placeholder="请输入手机号码"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" @click="handleSendCode">
            发送验证码
          </a-button>
        </a-form-item>
      </a-form>
    </div>

    <!-- 步骤2：输入验证码 -->
    <div v-else :tabindex="-1" class="form-container">
      <a-form :model="formState" layout="vertical">
        <a-form-item label="验证码">
          <a-input
            v-model:value="formState.verificationCode"
            placeholder="请输入验证码"
          />
        </a-form-item>
        <a-form-item label="二次验证密码">
          <a-input
            v-model:value="formState.password"
            placeholder="如果开启了二次验证，请输入密码"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button @click="currentStep = 1">上一步</a-button>
            <a-button type="primary" @click="handleConfirm">
              确认添加
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </div>

    <!-- 操作结果显示 -->
    <a-divider />
    <a-textarea
      v-model:value="resultLog"
      :rows="4"
      readonly
      :auto-size="{ minRows: 4, maxRows: 6 }"
    />
  </a-modal>
</template>

<script lang="ts" setup>
import { ref, reactive, defineProps, defineEmits } from "vue";
import { TelegramClient } from "@/websocket/Client";

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", visible: boolean): void;
  (e: "success"): void;
}>();

const currentStep = ref(1);
const resultLog = ref("");

const formState = reactive({
  phoneNumber: "",
  password: "",
  verificationCode: "",
});

// 添加手机号验证函数
const validatePhone = async (_rule: any, value: string) => {
  if (!value || value.trim() === '') {
    throw new Error('请输入手机号码');
  }
};

// 发送验证码
const handleSendCode = async () => {
  try {
    // 验证手机号
    if (!formState.phoneNumber || formState.phoneNumber.trim() === '') {
      const timestamp = new Date().toLocaleTimeString();
      resultLog.value += `[${timestamp}] 错误: 请输入有效的手机号码\n`;
      return;
    }

    const username = localStorage.getItem("username");
    if (!username) {
      throw new Error("未找到登录用户名");
    }
    const client = TelegramClient.getInstance();
    // 这里需要调用相应的WebSocket方法发送手机号
    const res = await client.sendPhoneNumber(username, formState.phoneNumber);
    const timestamp = new Date().toLocaleTimeString();
    resultLog.value = `[${timestamp}] 已发送验证码到手机: ${formState.phoneNumber}\n`;
    resultLog.value += `${JSON.stringify(res)}\n`;
    currentStep.value = 2;
  } catch (error) {
    const timestamp = new Date().toLocaleTimeString();
    resultLog.value += `[${timestamp}] 发送验证码失败: ${error}\n`;
  }
};

// 确认验证码
const handleConfirm = async () => {
  try {
    const username = localStorage.getItem("username");
    if (!username) {
      throw new Error("未找到登录用户名");
    }

    const client = TelegramClient.getInstance();
    // 这里需要调用相应的WebSocket方法验证验证码
    const res = await client.confirmVerificationCode(
      username,
      formState.phoneNumber,
      formState.password,
      formState.verificationCode
    );

    const timestamp = new Date().toLocaleTimeString();
    resultLog.value += `[${timestamp}] 验证中...\n`;
    resultLog.value += `${JSON.stringify(res)}\n`;

    // 只有当服务器返回成功状态时才触发success事件并关闭窗口
    if (res.nCode === 1) {  // 根据实际API响应结构调整判断条件
      resultLog.value += `[${timestamp}] 验证成功，账号已添加\n`;
      emit("success");
      resetAndClose();
    } else {
      resultLog.value += `[${timestamp}] 验证失败: ${res.strResult}\n`;  // 假设错误信息在res.message中
    }
  } catch (error) {
    const timestamp = new Date().toLocaleTimeString();
    resultLog.value += `[${timestamp}] 验证失败: ${error}\n`;
  }
};

// 添加关闭前的焦点处理
const handleBeforeClose = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

// 修改取消操作
const handleCancel = () => {
  handleBeforeClose();
  resetAndClose();
};

// 修改重置并关闭弹窗
const resetAndClose = () => {
  handleBeforeClose();
  currentStep.value = 1;
  formState.phoneNumber = "";
  formState.password = "";
  formState.verificationCode = "";
  resultLog.value = "";
  emit("update:visible", false);
};
</script>

<style scoped>
.form-container {
  outline: none;
  padding: 10px;
}
</style>
